import type {
  CampaignEmailDayOfWeek,
  CampaignEmailFrequency,
  CampaignEmailSectionsConfig,
} from '../types/campaign-email.types';

export const CAMPAIGN_EMAIL_FREQUENCIES: Array<{ value: CampaignEmailFrequency; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

export const CAMPAIGN_EMAIL_DAILY_FREQUENCY: { value: CampaignEmailFrequency; label: string } = {
  value: 'daily',
  label: 'Daily (testing only)',
};

export function getCampaignEmailFrequencyOptions(includeDaily: boolean) {
  if (!includeDaily) {
    return CAMPAIGN_EMAIL_FREQUENCIES;
  }
  return [CAMPAIGN_EMAIL_DAILY_FREQUENCY, ...CAMPAIGN_EMAIL_FREQUENCIES];
}

export const CAMPAIGN_EMAIL_DAY_OF_WEEK_OPTIONS: Array<{
  value: CampaignEmailDayOfWeek;
  label: string;
}> = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

export const CAMPAIGN_EMAIL_METRIC_OPTIONS = [
  { value: 'spend', label: 'Spend' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'ctr', label: 'CTR' },
  { value: 'cpm', label: 'CPM' },
  { value: 'cpa', label: 'CPA' },
  { value: 'conversions', label: 'Conversions' },
];

export const CAMPAIGN_EMAIL_CHART_METRIC_OPTIONS = [
  { value: 'spend', label: 'Spend' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
];

/** Quick-start prompts that replace the AI custom instructions textarea. */
export const CAMPAIGN_EMAIL_AI_INSTRUCTION_PRESETS = [
  {
    id: 'spend-efficiency',
    label: 'Focus on spend efficiency',
    instruction:
      'Focus on where spend is working hardest. Highlight efficient campaigns in a positive light and suggest gentle opportunities to lean into what is working.',
  },
  {
    id: 'wow-changes',
    label: 'Highlight week-over-week changes',
    instruction:
      'Highlight week-over-week movement in a friendly way. Celebrate gains and, where figures softened, offer a constructive explanation rather than calling out problems.',
  },
  {
    id: 'standout-campaigns',
    label: 'Spotlight standout campaigns',
    instruction:
      'Spotlight the campaigns that stood out this period. Keep the tone encouraging and high-level, with bold campaign names and a few key figures.',
  },
  {
    id: 'executive-concise',
    label: 'Keep it concise for executives',
    instruction:
      'Keep the overview short and executive-friendly. Lead with a couple of clear takeaways, stay positive, and avoid technical jargon.',
  },
] as const;

export const DEFAULT_SECTIONS_CONFIG: CampaignEmailSectionsConfig = {
  summary_metrics: ['spend', 'impressions', 'clicks'],
  include_chart: true,
  chart_metrics: ['spend'],
};

export const EMPTY_CAMPAIGN_EMAIL_FORM_VALUES = {
  name: '',
  clientId: '',
  selectedCampaignIds: [],
  frequency: 'weekly' as CampaignEmailFrequency,
  dayOfWeek: 0 as CampaignEmailDayOfWeek,
  startDate: '',
  endDate: '',
  sectionsConfig: DEFAULT_SECTIONS_CONFIG,
  aiNarrativeEnabled: true,
  customPromptInstructions: '',
  brandingOverride: '',
  useBrandingOverride: false,
  portalContactIds: [],
  manualRecipients: [],
  scheduleStatus: 'active' as const,
};
